import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import OffersTag from '../../../assets/icons/OffersTag';
import {
  TileWrapper,
  BuildCta,
  TagAndBuildWrapper,
  NameAndTrimWrapper,
  ImageWrapper,
  VehicleImage,
  MSRPWrapper,
  CreditContainer,
  CreditCTA,
  CTAsWrapper,
  MainCta,
  InventoryCTA,
  PriceCopy,
  OfferAndCtaWrapper,
  BonusOfferTitle,
} from './VehicleTile.styles';
import {
  TagLabel,
  CopyOne,
  CopyTwo,
  StaticHeadlineOrderTwo,
  TextCTA,
} from '../../Common/Common.styles';
import { COLORS } from '../../../styles/variables';
import PricingOptions from './PricingOptions/PricingOptions';
import { PromotionType } from '../../../types/offers';
import { priceFormat } from '../../../utils/formatPrice';
import { parseDigitalLeadsData } from '../../../utils/digitalLeadsFormat';
import { selectZipCode } from '../../../reducers/zipCodeReducer';
import formatModelName from '../../../utils/formatModelName';
import removeYearFromModelName from '../../../utils/removeYearFromModelName';
import { selectDealerContextId } from '../../../reducers/dealerContextReducer';
import { getConfiguratorUrl } from '../../../utils/build-url';
import Modal from '../../Modal';
import { IS_TIER_1 } from '../../../constants/environment';
import useCtaUrls from '../../../hooks/useCtaUrls';

type VehicleTileProps = {
  offer: PromotionType;
  offerType: string;
};

const VehicleTile: React.FC<VehicleTileProps> = ({ offer, offerType }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStackableOffers, setIsStackableOffer] = useState(false);

  const {
    programToDate,
    customerDownPayment,
    monthlyPayment,
    term,
    aprRate,
    creditBonus,
    programName,
  } = offer;
  const { modelYear, trimline, modelCodeDescription } = offer.offerModelDetail;
  const { t } = useTranslation();

  const { confirmedZipcode, market } = useSelector(selectZipCode);
  const { dealerId } = useSelector(selectDealerContextId);

  const isCaMarket = market === 'CA';

  const trimmedModelName = removeYearFromModelName(modelYear, modelCodeDescription);

  const handleOpenModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    const buttonId = event.currentTarget.id;
    if (buttonId === 'bonus-offer') setIsStackableOffer(true);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsStackableOffer(false);
  };

  const inventoryUrl = useCtaUrls(offer);

  const digitalLeadsInfo = useMemo(() => {
    const dataInfo = parseDigitalLeadsData(offer, isCaMarket, confirmedZipcode, dealerId);
    return dataInfo;
  }, [confirmedZipcode, offer, dealerId, isCaMarket]);

  const renderBuildYourAudiCTA = useMemo(() => {
    const target = getConfiguratorUrl(offer?.offerModelDetail);
    if (target) {
      return (
        <BuildCta
          href={target}
          variant="tertiary"
          size="sm"
          data-testid="build-cta"
          target="_blank"
          icon="external-link"
        >
          Build Your Audi
        </BuildCta>
      );
    }
    return null;
  }, [offer]);

  const hasCreditOption = offer.stackableOffers.length;

  const tileImage = useMemo(() => {
    const image = offer.offerImageUrl;
    if (image?.includes('wid=2238')) return `${image}&fmt=png-alpha`;
    if (!image)
      return 'https://nar.media.audi.com/is/image/audinar/nemo/us/offers/fallbacks/fallback_car_default.png';
    return `${image}?fmt=png-alpha`;
  }, [offer.offerImageUrl]);

  const trim = useMemo(() => {
    if (!!offerType && offerType === 'lease' && trimline?.toLocaleLowerCase() !== 'base') {
      return trimline;
    }
    return null;
  }, [offerType, trimline]);
  const msrp = priceFormat(offer?.offerModelDetail?.msrp);

  const isBonusOffer = offerType === 'credit';

  const creditOfferLabel = useMemo(() => {
    if (programName === 'AUD_EV_CREDIT1') return 'EV Lease Bonus';
    if (programName === 'AUD_SOA_COMBINED') return 'Season of Audi Bonus + Audi Credit';
    if (programName === 'AUD_SALES_EVENT') return 'Season of Audi Customer Bonus';
    if (programName === 'AUD_COSTCO_SELECT_PROGRAM') return 'Costco Select Bonus';
    if (programName === 'AUD_CUSTOMER_CREDIT') return 'Customer Bonus';
    return 'Audi Credit';
  }, [programName]);

  const bonusOfferTitle = useMemo(() => {
    const bonusValue = priceFormat(creditBonus);
    return `${bonusValue} ${creditOfferLabel}`;
  }, [creditBonus, creditOfferLabel]);

  return (
    <>
      <TileWrapper
        data-promotion-type={offerType} // either lease, finane, or credit
        data-promotion-offerdate={programToDate} // validity date of offer
        data-promotion-credit={offer.creditBonus} // credit bonus offer // ??
        data-promotion-lease-downpayment={customerDownPayment?.toString() ?? 'null'} // downpayment for lease offer
        data-promotion-lease-payment={monthlyPayment?.toString() ?? 'null'} // monthly payment for lease offer
        data-promotion-lease-termlength={offerType === 'lease' ? term : 'null'} // term length for lease offer
        data-promotion-finance-apr={offerType === 'finance' ? aprRate : 'null'} // apr for finance offer
        data-promotion-finance-termlength={offerType === 'finance' ? term : 'null'} // termlength for finance offer
        data-vehicle-model-name={trimmedModelName} // name of model -excludes year // ?
        data-vehicle-model-year={modelYear} // year of model
        data-vehicle-price={msrp !== '-' ? msrp : 'null'} // price of vehicle // ?
        data-vehicle-trim={trimline} // vehicle trim
        data-testid={`offer-${'id'}-tile-wrapper`}
      >
        <TagAndBuildWrapper>
          <TagLabel data-testid={`${offerType}-tag`}>
            {isBonusOffer && <OffersTag size="small" />}
            {t(`promo.tile.offerType.${offerType}`)}
          </TagLabel>
          {IS_TIER_1 && renderBuildYourAudiCTA}
        </TagAndBuildWrapper>
        <NameAndTrimWrapper>
          <StaticHeadlineOrderTwo data-testid="offer-name" isBold color={COLORS.black}>
            {formatModelName(modelCodeDescription)}
          </StaticHeadlineOrderTwo>
          <CopyOne data-testid="offer-trimname">{trim}</CopyOne>
        </NameAndTrimWrapper>
        <ImageWrapper>
          <VehicleImage
            data-testid="offer-hero-image"
            src={tileImage}
            alt="vehicle-sample-img"
            isImageExist={!!offer.offerImageUrl}
          />
        </ImageWrapper>
        <MSRPWrapper>
          {offerType === 'lease' && (
            <>
              <CopyTwo data-testid="offer-msrp-label">{t('promo.tile.msrp')}</CopyTwo>
              <PriceCopy data-testid="offer-msrp-price" isBold>
                {msrp}
              </PriceCopy>
            </>
          )}
        </MSRPWrapper>
        {isBonusOffer && <BonusOfferTitle>{bonusOfferTitle}</BonusOfferTitle>}
        <OfferAndCtaWrapper>
          <PricingOptions offer={offer} offerType={offerType} showOfferDetails={handleOpenModal} />
          <CreditContainer>
            {!!hasCreditOption && (
              <CreditCTA tabIndex={0} onClick={handleOpenModal} id="bonus-offer">
                <OffersTag data-testid="offer-bonus-tag" size="large" />
                <TextCTA
                  data-testid="offer-eligible-cta"
                  color={COLORS.grey60}
                  size="medium"
                  isBold
                  tabIndex={-1}
                >
                  {t('promo.offers.viewEligibleBonusOfferCta')}
                </TextCTA>
              </CreditCTA>
            )}
          </CreditContainer>
          <CTAsWrapper>
            <MainCta
              id="digital-leads"
              data-info={digitalLeadsInfo}
              data-testid="offer-contact-cta"
            >
              {t('promo.offers.cta.contact')}
            </MainCta>
            <InventoryCTA
              data-testid="offer-inventory-cta"
              variant="secondary"
              href={inventoryUrl}
              iconPosition="trailing"
              icon="external-link"
              target="_blank"
              isFullWidth
            >
              {t('promo.offers.cta.inventory')}
            </InventoryCTA>
          </CTAsWrapper>
        </OfferAndCtaWrapper>
      </TileWrapper>
      {isModalOpen && (
        <Modal
          offer={offer}
          inventoryUrl={inventoryUrl}
          onCloseModal={handleCloseModal}
          dealerId={dealerId}
          isStackableOffers={isStackableOffers}
        />
      )}
    </>
  );
};

export default VehicleTile;
